/**
 * API Client - Base configuration for all API calls
 *
 * This module provides a centralized HTTP client with:
 * - Automatic token injection
 * - Global error handling
 * - Session management
 * - Request/response interceptors
 */

import {ApiDetails, KEY} from '../../Constants';
import CryptoJS from 'crypto-js';
import {handle403Forbidden, handle500ServerError} from '../../utils/authErrorHandler';

/**
 * Get authentication token from encrypted localStorage
 * @returns {string|null} Authentication token or null if not found
 */
export const getAuthToken = () => {
    try {
        const encryptedData = localStorage.getItem("myMap");
        if (!encryptedData) {
            console.warn('[AUTH] No encrypted data found in localStorage');
            return null;
        }

        const decrypted = CryptoJS.AES.decrypt(encryptedData, KEY);
        const originalText = decrypted.toString(CryptoJS.enc.Utf8);
        const userData = JSON.parse(originalText);

        // Search for access_token by key name (works for both login and signup)
        const token = userData.find(([key]) => key === 'access_token')?.[1];

        // Fallback to index 28 for backward compatibility with older sessions
        const finalToken = token || userData[28]?.[1] || null;

        if (!finalToken) {
            console.warn('[AUTH] Token not found in user data. Available keys:', userData.map(([key]) => key));
        } else {
            console.log('[AUTH] Token retrieved successfully:', finalToken ? 'Token exists (length: ' + finalToken.length + ')' : 'Token is empty');
        }

        return finalToken;
    } catch (error) {
        console.error('[AUTH] Error getting auth token:', error);
        return null;
    }
};

/**
 * Get landlord ID from encrypted localStorage
 * @returns {string|null} Landlord ID or null if not found
 */
export const getLandlordId = () => {
    try {
        const encryptedData = localStorage.getItem("myMap");
        if (!encryptedData) return null;

        const decrypted = CryptoJS.AES.decrypt(encryptedData, KEY);
        const originalText = decrypted.toString(CryptoJS.enc.Utf8);
        const userData = JSON.parse(originalText);

        // Landlord ID is at index 0 based on existing codebase pattern
        return userData[0]?.[1] || null;
    } catch (error) {
        console.error('Error getting landlord ID:', error);
        return null;
    }
};

/**
 * Get user email from encrypted localStorage
 * @returns {string|null} Email address or null if not found
 */
export const getUserEmail = () => {
    try {
        const encryptedData = localStorage.getItem("myMap");
        if (!encryptedData) return null;

        const decrypted = CryptoJS.AES.decrypt(encryptedData, KEY);
        const originalText = decrypted.toString(CryptoJS.enc.Utf8);
        const userData = JSON.parse(originalText);

        // Email is at index 5 based on existing codebase pattern
        return userData[5]?.[1] || null;
    } catch (error) {
        console.error('Error getting user email:', error);
        return null;
    }
};

/**
 * Get full decrypted user data from localStorage
 * @returns {Array|null} User data array or null if not found
 */
export const getUserData = () => {
    try {
        const encryptedData = localStorage.getItem("myMap");
        if (!encryptedData) return null;

        const decrypted = CryptoJS.AES.decrypt(encryptedData, KEY);
        const originalText = decrypted.toString(CryptoJS.enc.Utf8);
        return JSON.parse(originalText);
    } catch (error) {
        console.error('Error getting user data:', error);
        return null;
    }
};

/**
 * Get sub-user ID from encrypted localStorage
 * @returns {number|null} Sub-user ID as integer or null if not found
 */
export const getSubUserId = () => {
    try {
        const encryptedData = localStorage.getItem("myMap");
        if (!encryptedData) return null;

        const decrypted = CryptoJS.AES.decrypt(encryptedData, KEY);
        const originalText = decrypted.toString(CryptoJS.enc.Utf8);
        const userData = JSON.parse(originalText);

        // Sub-user ID location in userData array (update index if needed)
        // This will need to be verified based on backend response structure
        const subUserId = userData.find(([key]) => key === 'sub_user_id')?.[1];

        // Parse as integer to ensure it's a whole number (3 instead of 3.0)
        return subUserId ? parseInt(subUserId, 10) : null;
    } catch (error) {
        console.error('Error getting sub-user ID:', error);
        return null;
    }
};

/**
 * Get user type from encrypted localStorage
 * @returns {string|null} User type or null if not found
 */
export const getUserType = () => {
    try {
        const encryptedData = localStorage.getItem("myMap");
        if (!encryptedData) return null;

        const decrypted = CryptoJS.AES.decrypt(encryptedData, KEY);
        const originalText = decrypted.toString(CryptoJS.enc.Utf8);
        const userData = JSON.parse(originalText);

        // User type location in userData array (update key if needed)
        return userData.find(([key]) => key === 'user_type')?.[1] || null;
    } catch (error) {
        console.error('Error getting user type:', error);
        return null;
    }
};

/**
 * Check if current user is a sub-user
 * @returns {boolean} True if user is a sub-user, false otherwise
 */
export const isSubUser = () => {
    const userType = getUserType();
    const subUserId = getSubUserId();

    // User is a sub-user if they have a sub_user_id or if user_type indicates sub-user
    return !!(subUserId || (userType && userType !== 'landlord'));
};

/**
 * Handle 401 Unauthorized - session expired or invalid token
 * Clears localStorage and redirects to login
 */
const handle401Unauthorized = () => {
    localStorage.clear();
    window.location.href = "/";
};

/**
 * Make an API request
 * @param {string} endpoint - API endpoint (e.g., "pegasus/visionary/tenant/get/all/tenants")
 * @param {Object} options - Fetch options
 * @param {string} options.method - HTTP method (GET, POST, PUT, DELETE)
 * @param {Object} options.body - Request body (will be JSON stringified)
 * @param {Object} options.headers - Additional headers
 * @param {boolean} options.requiresAuth - Whether endpoint requires authentication (default: true)
 * @returns {Promise<Object>} Response data
 */
export const apiRequest = async (endpoint, options = {}) => {
    const {
        method = 'POST',
        body = null,
        headers = {},
        requiresAuth = true,
    } = options;

    // Build headers
    const requestHeaders = {
        'Content-Type': 'application/json',
        ...headers,
    };

    // Add auth token if required
    if (requiresAuth) {
        const token = getAuthToken();
        console.log(`[API REQUEST] Endpoint: ${endpoint}, Token retrieved:`, token ? `YES (length: ${token.length})` : 'NO');
        if (token) {
            requestHeaders.token = token;
            console.log('[API REQUEST] Token added to headers');
        } else {
            console.warn('[API REQUEST] No token available - request will fail if auth is required');
        }
    }

    // Build fetch options
    const fetchOptions = {
        method,
        headers: requestHeaders,
    };

    // Log the final headers being sent (redact token value for security)
    const headersForLog = {...requestHeaders};
    if (headersForLog.token) {
        headersForLog.token = `${headersForLog.token.substring(0, 10)}...`;
    }
    console.log(`[API REQUEST] ${method} ${endpoint}`, 'Headers:', headersForLog);

    // Add body for non-GET requests
    if (body && method !== 'GET') {
        fetchOptions.body = JSON.stringify(body);
    }

    try {
        const response = await fetch(`${ApiDetails}${endpoint}`, fetchOptions);

        // Handle authentication/authorization errors
        if (response.status === 401) {
            handle401Unauthorized();
            throw new Error('Unauthorized - session expired');
        }

        // Check if response is a file (blob) by content type
        const contentType = response.headers.get('content-type');
        const isFileResponse = contentType && (
            contentType.includes('application/octet-stream') ||
            contentType.includes('application/pdf') ||
            contentType.includes('application/vnd.openxmlformats-officedocument') ||
            contentType.includes('application/msword') ||
            contentType.includes('application/zip') ||
            contentType.includes('image/')
        );

        if (isFileResponse) {
            // For file downloads, return blob directly
            const blob = await response.blob();

            if (!response.ok) {
                // If response is not ok, try to parse error from blob
                const text = await blob.text();
                let errorMessage = 'Failed to download file';
                try {
                    const errorData = JSON.parse(text);
                    errorMessage = errorData?.Error || errorData?.Message || errorMessage;
                } catch (e) {
                    // If blob is not JSON, just use generic message
                }

                if (response.status === 403) {
                    handle403Forbidden(errorMessage);
                } else if (response.status === 500) {
                    handle500ServerError(errorMessage);
                }

                return {
                    data: null,
                    blob: null,
                    status: response.status,
                    ok: false,
                    error: errorMessage,
                };
            }

            return {
                data: null,
                blob: blob,
                status: response.status,
                ok: response.ok,
            };
        } else {
            // For JSON responses, parse as JSON
            const data = await response.json();

            if (response.status === 403) {
                // Extract custom error message from response body
                const errorMessage = data?.Error || data?.Message || data?.message || 'You do not have permission to perform this action.';
                handle403Forbidden(errorMessage);
                // Return the response so caller can handle it if needed
                return {
                    data,
                    status: response.status,
                    ok: false,
                };
            }

            if (response.status === 500) {
                const errorMessage = data?.Error || data?.Message || data?.message || 'An unexpected server error occurred.';
                handle500ServerError(errorMessage);
                throw new Error('Server error');
            }

            // Return both data and status for flexibility
            return {
                data,
                status: response.status,
                ok: response.ok,
            };
        }
    } catch (error) {
        console.error(`API request failed: ${endpoint}`, error);
        throw error;
    }
};

/**
 * Make a GET request
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Response data
 */
export const get = (endpoint, options = {}) => {
    return apiRequest(endpoint, {...options, method: 'GET'});
};

/**
 * Make a POST request
 * @param {string} endpoint - API endpoint
 * @param {Object} body - Request body
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Response data
 */
export const post = (endpoint, body, options = {}) => {
    return apiRequest(endpoint, {...options, method: 'POST', body});
};

/**
 * Make a PUT request
 * @param {string} endpoint - API endpoint
 * @param {Object} body - Request body
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Response data
 */
export const put = (endpoint, body, options = {}) => {
    return apiRequest(endpoint, {...options, method: 'PUT', body});
};

/**
 * Make a DELETE request
 * @param {string} endpoint - API endpoint
 * @param {Object} body - Request body
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Response data
 */
export const deleteRequest = (endpoint, body, options = {}) => {
    return apiRequest(endpoint, {...options, method: 'DELETE', body});
};

/**
 * Download file from API endpoint
 * @param {string} endpoint - API endpoint
 * @param {Object} body - Request body (optional)
 * @param {string} fileName - Default file name for download
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Response with blob or error
 */
export const downloadFile = async (endpoint, body = null, fileName = 'download', options = {}) => {
    const {requiresAuth = true, headers = {}} = options;

    const requestHeaders = {
        'Content-Type': 'application/json',
        ...headers,
    };

    // Add auth token if required
    if (requiresAuth) {
        const token = getAuthToken();
        if (token) {
            requestHeaders.token = token;
        }
    }

    const fetchOptions = {
        method: 'POST',
        headers: requestHeaders,
    };

    if (body) {
        fetchOptions.body = JSON.stringify(body);
    }

    try {
        const response = await fetch(`${ApiDetails}${endpoint}`, fetchOptions);

        // Handle authentication/authorization errors
        if (response.status === 401) {
            handle401Unauthorized();
            throw new Error('Unauthorized - session expired');
        }

        if (!response.ok) {
            const contentType = response.headers.get('content-type');
            let errorMessage = 'Failed to download file';

            if (contentType && contentType.includes('application/json')) {
                try {
                    const errorData = await response.json();
                    errorMessage = errorData?.Error || errorData?.Message || errorMessage;
                } catch (e) {
                    // Continue with default error message
                }
            }

            if (response.status === 403) {
                handle403Forbidden(errorMessage);
            } else if (response.status === 500) {
                handle500ServerError(errorMessage);
            }

            return {
                blob: null,
                status: response.status,
                ok: false,
                error: errorMessage,
            };
        }

        const blob = await response.blob();

        return {
            blob: blob,
            status: response.status,
            ok: response.ok,
        };
    } catch (error) {
        console.error(`File download failed: ${endpoint}`, error);
        throw error;
    }
};

/**
 * Upload file(s) to API
 * @param {string} endpoint - API endpoint
 * @param {FormData} formData - Form data with files
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Response data
 */
export const uploadFile = async (endpoint, formData, options = {}) => {
    const {requiresAuth = true, headers = {}} = options;

    const requestHeaders = {...headers};

    // Add auth token if required (don't set Content-Type for FormData)
    if (requiresAuth) {
        const token = getAuthToken();
        if (token) {
            requestHeaders.token = token;
        }
    }

    try {
        const response = await fetch(`${ApiDetails}${endpoint}`, {
            method: 'POST',
            headers: requestHeaders,
            body: formData,
        });

        // Handle authentication/authorization errors
        if (response.status === 401) {
            handle401Unauthorized();
            throw new Error('Unauthorized - session expired');
        }

        if (response.status === 403) {
            handle403Forbidden();
            // Don't throw - let caller handle the 403 response if needed
        }

        if (response.status === 500) {
            handle500ServerError();
            throw new Error('Server error');
        }

        const data = await response.json();

        return {
            data,
            status: response.status,
            ok: response.ok,
        };
    } catch (error) {
        console.error(`File upload failed: ${endpoint}`, error);
        throw error;
    }
};

export default {
    get,
    post,
    put,
    delete: deleteRequest,
    downloadFile,
    uploadFile,
    getAuthToken,
    getLandlordId,
    getUserEmail,
    getUserData,
    getSubUserId,
    getUserType,
    isSubUser,
};
